// ─────────────────────────────────────────────
//  GainersLosers.js — Market Movers
//  Data via api.js → Yahoo Finance screener
// ─────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchGainers, fetchLosers, fetchMostActive } from "../../config/api";
import "./GainersLosers.css";

const TABS = [
  { key: "GAINERS",     label: "Gainers",     color: "#22c55e" },
  { key: "LOSERS",      label: "Losers",      color: "#ef4444" },
  { key: "MOST_ACTIVE", label: "Most Active", color: "#3b82f6" },
];

export default function GainersLosers() {
  const [activeTab, setActiveTab] = useState("GAINERS");
  const [data,      setData]      = useState({ GAINERS: [], LOSERS: [], MOST_ACTIVE: [] });
  const [loading,   setLoading]   = useState(true);
  const navigate = useNavigate();

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [g, l, a] = await Promise.allSettled([
        fetchGainers(),
        fetchLosers(),
        fetchMostActive(),
      ]);
      setData({
        GAINERS:     g.status === "fulfilled" ? (g.value?.trends || []) : [],
        LOSERS:      l.status === "fulfilled" ? (l.value?.trends || []) : [],
        MOST_ACTIVE: a.status === "fulfilled" ? (a.value?.trends || []) : [],
      });
    } catch (e) {
      console.error("GainersLosers:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    const id = setInterval(loadAll, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [loadAll]);

  const rows = data[activeTab];

  // Strip Yahoo .NS / .BO suffix for routing
  const cleanSym = (s = "") => s.replace(/\.(NS|BO)$/, "");

  return (
    <div className="gl-wrap">
      <div className="gl-header">
        <span className="gl-title">Market Movers</span>
        <button className="gl-refresh" onClick={loadAll} title="Refresh">↻</button>
      </div>

      <div className="gl-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`gl-tab ${activeTab === t.key ? "active" : ""}`}
            style={activeTab === t.key ? { borderBottomColor: t.color, color: t.color } : {}}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="gl-table-wrap">
        <table className="gl-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Stock</th>
              <th>Price</th>
              <th className="gl-col-change">Change</th>
              <th>% Chg</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="gl-skel-row">
                    <td><div className="gl-skel" /></td>
                    <td><div className="gl-skel w60" /></td>
                    <td><div className="gl-skel w50" /></td>
                    <td><div className="gl-skel w40" /></td>
                    <td><div className="gl-skel w40" /></td>
                  </tr>
                ))
              : rows.map((item, i) => {
                  const change = parseFloat(item.change)         || 0;
                  const pct    = parseFloat(item.change_percent) || 0;
                  const isUp   = change >= 0;
                  const sym    = cleanSym(item.symbol);
                  return (
                    <tr
                      key={i}
                      className="gl-row"
                      onClick={() => navigate(`/stock/${sym}`)}
                      title={`Open ${item.name}`}
                    >
                      <td className="gl-rank">{i + 1}</td>
                      <td className="gl-name">
                        <span className="gl-stock-name">{item.name}</span>
                        <span className="gl-stock-sym">{sym}</span>
                      </td>
                      <td className="gl-price">
                        ₹{Number(item.price || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </td>
                      <td className={`gl-change ${isUp ? "up" : "down"}`}>
                        {isUp ? "+" : ""}{Math.abs(change).toFixed(2)}
                      </td>
                      <td className={`gl-pct ${isUp ? "up" : "down"}`}>
                        <span
                          className="gl-pct-badge"
                          style={{ background: isUp ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)" }}
                        >
                          {isUp ? "▲" : "▼"} {Math.abs(pct).toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
        {!loading && rows.length === 0 && (
          <div className="gl-empty">No data available</div>
        )}
      </div>
    </div>
  );
}